import axios from "axios";

// Configure base URL for form APIs (ngrok URLs for testing)
// const BASE_URL = "https://b6ae1a777b09.ngrok-free.app/api/v1/admin";
const BASE_URL = `${import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '')}/admin`;

/**
 * Get authorization headers with token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    'authorization': token ? `Bearer ${token}` : "",
  };
};

/**
 * Configure axios instance with default headers
 */
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

/**
 * Form Service - Handles all CRUD operations for form management
 */

// Get all forms or forms by role
export const getFormsByRole = async (role = null) => {
  try {
    let url = `dynamic-forms?page=1&limit=100`;
    if (role) {
      url = `dynamic-form?role=${role.toLowerCase()}`;
    }
    const response = await axiosInstance.get(url, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching forms:", error);
    throw error;
  }
};


export const getApi = async (api) => {
  try {
    let url = `${api}`;
    const response = await axiosInstance.get(url, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Roles:", error);
    throw error;
  }
};

// Update an existing role
export const updateRole = async (roleId, roleData) => {
  try {
    const response = await axiosInstance.patch(`/plus-role/${roleId}`, roleData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating role:", error);
    throw error;
  }
};

// Delete a role
export const deleteRole = async (roleId) => {
  try {
    const response = await axiosInstance.delete(`/plus-role/${roleId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting role:", error);
    throw error;
  }
};

// Create a new form
export const createForm = async (api, formData) => {
  try {
    const response = await axiosInstance.post(
      `${api}`,
      formData,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating form:", error);
    throw error;
  }
};

// Get a single form by ID
export const getFormById = async (formId) => {
  try {
    const response = await axiosInstance.get(`/${formId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching form:", error);
    throw error;
  }
};

// Update an existing form
export const updateForm = async (formId, formData) => {
  try {
    const response = await axiosInstance.patch(`/dynamic-form/${formId}`, formData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error updating form:", error);
    throw error;
  }
};

// Delete a form
export const deleteForm = async (formId) => {
  try {
    const response = await axiosInstance.delete(`/dynamic-form/${formId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting form:", error);
    throw error;
  }
};

// Publish/Unpublish a form
export const publishForm = async (formId, isActive) => {
  try {
    const response = await axiosInstance.patch(
      `dynamic-form/${formId}/toggle-status`,
      { isActive },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error publishing form:", error);
    throw error;
  }
};

// Duplicate a form
export const duplicateForm = async (formId) => {
  try {
    const response = await axiosInstance.post(
      `/${formId}/duplicate`,
      {},
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error duplicating form:", error);
    throw error;
  }
};

// Export form as JSON
export const exportFormAsJSON = (form) => {
  const dataStr = JSON.stringify(form, null, 2);
  const dataUri =
    "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

  const exportFileDefaultName = `${form.role}_form_v${form.version}.json`;

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", exportFileDefaultName);
  linkElement.click();
};

// Import form from JSON
export const importFormFromJSON = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const form = JSON.parse(e.target.result);

        // Validate basic structure
        if (!form.role) {
          reject(new Error("Invalid form: 'role' field is required"));
          return;
        }

        if (!form.fields && !form.steps) {
          reject(new Error("Invalid form: Either 'fields' or 'steps' array is required"));
          return;
        }

        resolve(form);
      } catch (error) {
        // Provide more helpful error messages
        if (error instanceof SyntaxError) {
          reject(new Error(`Invalid JSON syntax: ${error.message}`));
        } else {
          reject(new Error(`Failed to parse JSON: ${error.message}`));
        }
      }
    };
    reader.onerror = () => reject(new Error("Error reading file"));
    reader.readAsText(file);
  });
};
